/**
 * PlatformSyncManager — Reverse Configuration Engine UI
 * Gestiona escaneo reverso de endpoints, OAuth SSO, sincronización de usuarios
 * y OkWallet, webhooks y verificación completa entre plataformas vinculadas.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  RefreshCw, Zap, Shield, Users, Wallet, Globe, Webhook,
  CheckCircle2, XCircle, AlertTriangle, Play, ArrowRight,
  Link2, Key, BarChart3, Activity, Bot, ChevronDown, ChevronUp,
  Copy, ExternalLink, Server, Lock, Unlock, TrendingUp, 
  TrendingDown, Database, Settings2, Eye, Send, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METHOD_COLORS = {
  GET:    'bg-green-500/20 text-green-300 border-green-500/30',
  POST:   'bg-blue-500/20 text-blue-300 border-blue-500/30',
  PUT:    'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
  DELETE: 'bg-red-500/20 text-red-300 border-red-500/30',
  PATCH:  'bg-purple-500/20 text-purple-300 border-purple-500/30',
};

const STATUS_COLORS = {
  ok:       { bg: 'bg-green-500/10 border-green-500/30',  text: 'text-green-400',  icon: CheckCircle2 },
  warning:  { bg: 'bg-yellow-500/10 border-yellow-500/30', text: 'text-yellow-400', icon: AlertTriangle },
  error:    { bg: 'bg-red-500/10 border-red-500/30',      text: 'text-red-400',    icon: XCircle },
  pending:  { bg: 'bg-gray-500/10 border-gray-500/30',    text: 'text-gray-400',   icon: RefreshCw },
  active:   { bg: 'bg-green-500/10 border-green-500/30',  text: 'text-green-400',  icon: CheckCircle2 },
};

function StatusBadge({ status, label }) {
  const cfg = STATUS_COLORS[status] || STATUS_COLORS.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${cfg.bg} ${cfg.text}`}>
      <Icon className="w-3 h-3" />{label || status}
    </span>
  );
}

function ScoreRing({ score = 0, size = 80 }) {
  const radius = 30, cx = size / 2, cy = size / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 90 ? '#22C55E' : score >= 70 ? '#3B82F6' : score >= 50 ? '#F59E0B' : '#EF4444';
  return (
    <svg width={size} height={size}>
      <circle cx={cx} cy={cy} r={radius} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="6" />
      <circle cx={cx} cy={cy} r={radius} fill="none" stroke={color} strokeWidth="6"
        strokeDasharray={circumference} strokeDashoffset={offset}
        strokeLinecap="round" transform={`rotate(-90 ${cx} ${cy})`} style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
      <text x={cx} y={cy + 5} textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">{score}%</text>
    </svg>
  );
}

// ── Tabs configuration ────────────────────────────────────────────────────────
const TABS = [
  { id: 'overview',   label: 'Resumen',         icon: BarChart3 },
  { id: 'endpoints',  label: 'Endpoints',        icon: Server },
  { id: 'oauth',      label: 'OAuth SSO',        icon: Key },
  { id: 'users',      label: 'Usuarios',         icon: Users },
  { id: 'wallet',     label: 'OkWallet',         icon: Wallet },
  { id: 'webhooks',   label: 'Webhooks',         icon: Webhook },
  { id: 'agent',      label: 'OkBot Agent',      icon: Bot },
  { id: 'monitor',    label: 'Monitor',          icon: Activity },
  { id: 'registry',   label: 'Registro',         icon: Database },
  { id: 'verify',     label: 'Verificación',     icon: Shield },
];

// ── Main Component ────────────────────────────────────────────────────────────
export default function PlatformSyncManager({ platforms = [] }) {
  const [selectedId, setSelectedId]         = useState(platforms[0]?.id || null);
  const [activeTab, setActiveTab]           = useState('overview');
  const [loading, setLoading]               = useState({});
  const [syncReport, setSyncReport]         = useState(null);
  const [endpointData, setEndpointData]     = useState(null);
  const [oauthStatus, setOauthStatus]       = useState(null);
  const [userSync, setUserSync]             = useState(null);
  const [walletReport, setWalletReport]     = useState(null);
  const [webhooks, setWebhooks]             = useState(null);
  const [verification, setVerification]     = useState(null);
  const [epExpanded, setEpExpanded]         = useState({});
  const [epSearch, setEpSearch]             = useState('');
  const [agentStatus, setAgentStatus]       = useState(null);
  const [monitorStatus, setMonitorStatus]   = useState(null);
  const [syncRegistry, setSyncRegistry]     = useState(null);
  const [safeResyncDry, setSafeResyncDry]   = useState(true);
  const [safeModules, setSafeModules]       = useState(['users','wallet','webhooks','endpoints']);

  const platform = platforms.find(p => p.id === selectedId);

  // Load everything when platform changes
  useEffect(() => {
    if (!selectedId) return;
    loadSyncReport();
    loadOauthStatus();
    loadUserSync();
    loadWalletReport();
    loadWebhooks();
    loadVerification();
    loadEndpointStats();
    loadAgentStatus();
    loadMonitorStatus();
    loadSyncRegistry();
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const setLoad = (key, val) => setLoading(l => ({ ...l, [key]: val }));

  const loadSyncReport = async () => {
    try {
      const r = await axios.get(`${API}/admin/platform-sync/${selectedId}/sync-report`);
      setSyncReport(r.data);
    } catch (e) {}
  };
  const loadOauthStatus = async () => {
    try {
      const r = await axios.get(`${API}/admin/platform-sync/${selectedId}/oauth-status`);
      setOauthStatus(r.data);
    } catch (e) {}
  };
  const loadUserSync = async () => {
    try {
      const r = await axios.get(`${API}/admin/platform-sync/${selectedId}/sync-users/status`);
      setUserSync(r.data);
    } catch (e) {}
  };
  const loadWalletReport = async () => {
    try {
      const r = await axios.get(`${API}/admin/platform-sync/${selectedId}/wallet-report`);
      setWalletReport(r.data);
    } catch (e) {}
  };
  const loadWebhooks = async () => {
    try {
      const r = await axios.get(`${API}/admin/platform-sync/${selectedId}/webhooks`);
      setWebhooks(r.data);
    } catch (e) {}
  };
  const loadVerification = async () => {
    try {
      const r = await axios.get(`${API}/admin/platform-sync/${selectedId}/verify`);
      if (r.data && r.data.overall_score !== undefined) setVerification(r.data);
    } catch (e) {}
  };
  const loadEndpointStats = async () => {
    try {
      const r = await axios.get(`${API}/admin/platform-sync/${selectedId}/endpoint-stats`);
      setEndpointData(r.data);
    } catch (e) {}
  };
  const loadAgentStatus = async () => {
    try {
      const r = await axios.get(`${API}/admin/platform-sync/${selectedId}/agent-status`);
      setAgentStatus(r.data?.status !== 'not_installed' ? r.data : null);
    } catch (e) {}
  };
  const loadMonitorStatus = async () => {
    try {
      const r = await axios.get(`${API}/admin/platform-sync/${selectedId}/monitor-status`);
      setMonitorStatus(r.data?.status !== 'never_run' ? r.data : null);
    } catch (e) {}
  };
  const loadSyncRegistry = async () => {
    try {
      const r = await axios.get(`${API}/admin/platform-sync/${selectedId}/sync-registry`);
      setSyncRegistry(r.data);
    } catch (e) {}
  };

  const run = async (key, fn) => {
    setLoad(key, true);
    try {
      await fn();
      toast.success('Operación completada ✓');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en la operación');
    } finally {
      setLoad(key, false);
    }
  };

  const handleReverseScan = () => run('scan', async () => {
    const r = await axios.post(`${API}/admin/platform-sync/${selectedId}/reverse-scan`);
    toast.success(`${r.data.available} endpoints disponibles de ${r.data.total_discovered} escaneados`);
    loadSyncReport(); loadEndpointStats();
  });

  const handleConfigureOAuth = () => run('oauth', async () => {
    const r = await axios.post(`${API}/admin/platform-sync/${selectedId}/configure-oauth`);
    setOauthStatus({ status: 'configured', config: r.data });
    toast.success('OAuth SSO configurado en ambas plataformas');
  });

  const handleTestOAuth = () => run('oauthTest', async () => {
    const r = await axios.post(`${API}/admin/platform-sync/${selectedId}/test-oauth`);
    toast[r.data.ok ? 'success' : 'error'](r.data.ok ? 'OAuth SSO funcional ✓' : `OAuth parcial: ${r.data.result}`);
    loadOauthStatus();
  });

  const handleSyncUsers = () => run('users', async () => {
    const r = await axios.post(`${API}/admin/platform-sync/${selectedId}/sync-users`);
    toast.success(`${r.data.matched} usuarios vinculados, ${r.data.new_remote} nuevos remotos`);
    loadUserSync();
  });

  const handleSyncWallet = () => run('wallet', async () => {
    const r = await axios.post(`${API}/admin/platform-sync/${selectedId}/sync-wallet`);
    toast.success(`${r.data.matched_wallets} wallets sincronizadas`);
    loadWalletReport();
  });

  const handleCreateWebhooks = () => run('webhooks', async () => {
    const r = await axios.post(`${API}/admin/platform-sync/${selectedId}/create-webhooks`);
    toast.success(`${r.data.total} webhooks creados, ${r.data.registered} registrados`);
    loadWebhooks();
  });

  const handleTestWebhook = async (webhookId) => {
    setLoad(`wh_${webhookId}`, true);
    try {
      const r = await axios.post(`${API}/admin/platform-sync/${selectedId}/test-webhook/${webhookId}`);
      toast[r.data.ok ? 'success' : 'error'](r.data.ok ? `Webhook OK (${r.data.status_code})` : `Error: ${r.data.error}`);
      loadWebhooks();
    } catch (e) {
      toast.error('Error al probar webhook');
    } finally {
      setLoad(`wh_${webhookId}`, false);
    }
  };

  const handleVerify = () => run('verify', async () => {
    const r = await axios.post(`${API}/admin/platform-sync/${selectedId}/verify`);
    setVerification(r.data);
    toast[r.data.overall_score >= 70 ? 'success' : 'warning'](
      `Verificación completada: ${r.data.overall_score}% (${r.data.health})`
    );
  });

  const handlePushReverseLink = () => run('reverseLink', async () => {
    const r = await axios.post(`${API}/admin/platform-sync/${selectedId}/push-reverse-link`);
    toast[r.data.success ? 'success' : 'warning'](
      r.data.success
        ? `✓ OkBot registrado como plataforma vinculada en el sistema remoto`
        : `Vinculación reversa parcial — el sistema remoto puede no tener el endpoint de linking`
    );
    loadSyncRegistry();
  });

  const handleInstallAgent = () => run('agentInstall', async () => {
    const r = await axios.post(`${API}/admin/platform-sync/${selectedId}/install-okbot-agent`);
    setAgentStatus(r.data);
    toast[r.data.installation_score >= 60 ? 'success' : 'warning'](
      `OkBot Agent: ${r.data.installation_score}% instalado (${r.data.steps?.length} pasos)`
    );
    loadSyncRegistry();
  });

  const handleRunMonitor = () => run('monitor', async () => {
    const r = await axios.post(`${API}/admin/platform-sync/${selectedId}/run-monitor`);
    setMonitorStatus(r.data);
    const newEps = r.data.new_endpoints || 0;
    const newWhs = r.data.missing_webhooks_fixed || 0;
    toast[r.data.status === 'clean' ? 'success' : 'info'](
      r.data.status === 'clean'
        ? 'Monitor: todo sincronizado, sin cambios detectados ✓'
        : `Monitor: ${newEps} endpoints nuevos, ${newWhs} webhooks auto-creados`
    );
    loadSyncReport(); loadWebhooks(); loadSyncRegistry();
  });

  const handleSafeResync = () => run('safeResync', async () => {
    const r = await axios.post(`${API}/admin/platform-sync/${selectedId}/safe-resync`, {
      modules: safeModules,
      dry_run: safeResyncDry,
    });
    toast[r.data.status === 'completed' ? 'success' : 'warning'](
      `Re-sync ${safeResyncDry ? '(simulación)' : ''}: ${r.data.status} — ${Object.values(r.data.results).filter(v => v === 'resynced').length} módulos sincronizados`
    );
    loadSyncRegistry(); loadSyncReport();
  });

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Copiado'); };

  if (platforms.length === 0) {
    return (
      <div className="glass-panel p-12 text-center">
        <Link2 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400 text-lg font-medium mb-2">No hay plataformas vinculadas</p>
        <p className="text-gray-600 text-sm">Vincula una plataforma desde el panel de Plataformas Conectadas para usar Sync Manager.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Platform Selector */}
      <div className="glass-panel p-4 flex flex-wrap items-center gap-3">
        <span className="text-gray-400 text-sm">Plataforma:</span>
        <div className="flex flex-wrap gap-2 flex-1">
          {platforms.map(p => (
            <button key={p.id} onClick={() => setSelectedId(p.id)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-mono font-semibold transition-colors ${
                selectedId === p.id ? 'bg-red-500 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}>
              <span className={`w-2 h-2 rounded-full ${p.status === 'active' ? 'bg-green-400' : 'bg-red-400'}`} />
              {p.platform_code}
            </button>
          ))}
        </div>
        {platform && (
          <a href={platform.platform_url} target="_blank" rel="noopener noreferrer"
            className="text-gray-500 hover:text-white text-xs flex items-center gap-1 transition-colors">
            <ExternalLink className="w-3.5 h-3.5" />{platform.platform_url}
          </a>
        )}
      </div>

      {/* Quick Stats */}
      {syncReport && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Endpoints',    val: syncReport.endpoint_stats?.total_endpoints || 0, icon: Server,    color: 'text-blue-400'   },
            { label: 'OAuth',        val: syncReport.oauth?.status || 'pending',            icon: Key,       color: syncReport.oauth?.status === 'configured' || syncReport.oauth?.status === 'active' ? 'text-green-400' : 'text-yellow-400', isText: true },
            { label: 'Usuarios',     val: syncReport.user_sync?.matched || 0,               icon: Users,     color: 'text-purple-400' },
            { label: 'Wallets',      val: syncReport.wallet_sync?.matched_wallets || 0,     icon: Wallet,    color: 'text-amber-400'  },
            { label: 'Webhooks',     val: `${syncReport.webhooks?.active || 0}/${syncReport.webhooks?.total || 0}`, icon: Webhook, color: 'text-red-400', isText: true },
          ].map(({ label, val, icon: Icon, color, isText }) => (
            <div key={label} className="glass-panel p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-4 h-4 ${color}`} />
                <p className="text-gray-500 text-xs">{label}</p>
              </div>
              <p className={`font-bold text-lg ${color} capitalize`}>{isText ? val : val.toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors ${
                activeTab === t.id ? 'bg-red-500 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}>
              <Icon className="w-3.5 h-3.5" />{t.label}
            </button>
          );
        })}
      </div>

      {/* ── OVERVIEW TAB ── */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          {/* Action Row */}
          <div className="glass-panel p-4">
            <p className="text-sm font-semibold text-gray-200 mb-3">🔄 Configuración en Reversa — Ejecutar en Orden</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
              {[
                { label: 'Escaneo Reverso', key: 'scan',     icon: Server,    onClick: handleReverseScan,     color: 'bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 border-blue-500/30',    step: 1 },
                { label: 'Config OAuth',    key: 'oauth',    icon: Key,       onClick: handleConfigureOAuth,  color: 'bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 border-purple-500/30', step: 2 },
                { label: 'Sync Usuarios',   key: 'users',    icon: Users,     onClick: handleSyncUsers,       color: 'bg-green-500/20 text-green-300 hover:bg-green-500/30 border-green-500/30',   step: 3 },
                { label: 'Sync OkWallet',   key: 'wallet',   icon: Wallet,    onClick: handleSyncWallet,      color: 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 border-amber-500/30',   step: 4 },
                { label: 'Crear Webhooks',  key: 'webhooks', icon: Webhook,   onClick: handleCreateWebhooks,  color: 'bg-red-500/20 text-red-300 hover:bg-red-500/30 border-red-500/30',       step: 5 },
                { label: 'Verificar Todo',  key: 'verify',   icon: Shield,    onClick: handleVerify,          color: 'bg-white/5 text-gray-300 hover:bg-white/10 border-white/10',              step: 6 },
              ].map(({ label, key, icon: Icon, onClick, color, step }) => (
                <button key={key} onClick={onClick} disabled={loading[key]}
                  className={`flex flex-col items-center gap-2 p-3 rounded-xl border text-xs font-medium transition-colors ${color}`}>
                  <div className="relative">
                    {loading[key]
                      ? <RefreshCw className="w-5 h-5 animate-spin" />
                      : <Icon className="w-5 h-5" />
                    }
                    <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-black/60 text-[9px] flex items-center justify-center font-bold">{step}</span>
                  </div>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Verification Score */}
          {verification && (
            <div className="glass-panel p-5">
              <div className="flex items-center gap-4 mb-4">
                <ScoreRing score={verification.overall_score} size={80} />
                <div>
                  <h3 className="text-white font-semibold text-lg">Score de Integración</h3>
                  <p className="text-gray-500 text-sm capitalize">{verification.health} — {verification.summary.ok}/{verification.summary.total} checks OK</p>
                  <p className="text-gray-600 text-xs mt-1">Verificado: {verification.verified_at}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {(verification.checks || []).map(c => {
                  const cfg = STATUS_COLORS[c.status] || STATUS_COLORS.pending;
                  const Icon = cfg.icon;
                  return (
                    <div key={c.name} className={`p-3 rounded-xl border ${cfg.bg}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className={`w-3.5 h-3.5 ${cfg.text}`} />
                        <span className={`text-xs font-semibold ${cfg.text}`}>{c.name}</span>
                      </div>
                      <div className="w-full h-1 bg-white/10 rounded-full">
                        <div className="h-full rounded-full" style={{ width: `${c.score}%`, background: c.status === 'ok' ? '#22C55E' : c.status === 'warning' ? '#F59E0B' : '#EF4444' }} />
                      </div>
                      <p className="text-gray-500 text-xs mt-1 truncate">{c.detail}</p>
                    </div>
                  );
                })}
              </div>
              {verification.next_steps?.length > 0 && (
                <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-xl p-3 space-y-1">
                  <p className="text-yellow-400 text-xs font-semibold mb-2">📋 Pasos recomendados:</p>
                  {verification.next_steps.map((s, i) => (
                    <p key={i} className="text-gray-400 text-xs flex gap-2"><span className="text-yellow-500">{i+1}.</span>{s}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Sync Summary */}
          {syncReport && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="glass-panel p-4">
                <h4 className="text-sm font-semibold text-gray-200 mb-3">📊 Estadísticas de Endpoints</h4>
                <div className="space-y-2 text-sm">
                  {[
                    ['Total endpoints', syncReport.endpoint_stats?.total_endpoints || 0],
                    ['Llamadas totales', syncReport.endpoint_stats?.total_calls || 0],
                    ['Tasa de éxito', `${syncReport.endpoint_stats?.success_rate || 0}%`],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between">
                      <span className="text-gray-500">{k}</span>
                      <span className="text-white font-semibold">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="glass-panel p-4">
                <h4 className="text-sm font-semibold text-gray-200 mb-3">🔄 Estado de Sincronización</h4>
                <div className="space-y-2 text-sm">
                  {[
                    ['OAuth SSO', syncReport.oauth?.status || 'no configurado'],
                    ['Usuarios', syncReport.user_sync ? `${syncReport.user_sync.matched} vinculados` : 'no sincronizado'],
                    ['Wallets',  syncReport.wallet_sync ? `${syncReport.wallet_sync.matched_wallets} sincronizadas` : 'no sincronizado'],
                    ['Webhooks', `${syncReport.webhooks?.active || 0}/${syncReport.webhooks?.total || 0} activos`],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between">
                      <span className="text-gray-500">{k}</span>
                      <span className="text-white font-semibold capitalize">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── ENDPOINTS TAB ── */}
      {activeTab === 'endpoints' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={handleReverseScan} disabled={loading.scan}
              className="btn-primary flex items-center gap-2 text-sm">
              {loading.scan ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Server className="w-4 h-4" />}
              {loading.scan ? 'Escaneando...' : 'Ejecutar Escaneo Reverso'}
            </button>
            <div className="relative flex-1 min-w-48">
              <input value={epSearch} onChange={e => setEpSearch(e.target.value)}
                placeholder="Buscar endpoints..." className="input-glass w-full text-sm pl-3" />
            </div>
          </div>

          {/* Endpoint Stats Summary */}
          {endpointData && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { label: 'Total',       val: endpointData.summary.total_endpoints, color: 'text-white' },
                { label: 'GET',         val: endpointData.summary.get_endpoints,   color: 'text-green-400' },
                { label: 'POST',        val: endpointData.summary.post_endpoints,  color: 'text-blue-400' },
                { label: 'Llamadas',    val: endpointData.summary.total_calls,     color: 'text-purple-400' },
                { label: 'Éxito',       val: `${endpointData.summary.success_rate}%`, color: 'text-amber-400' },
              ].map(({ label, val, color }) => (
                <div key={label} className="glass-panel p-3 text-center">
                  <p className={`text-2xl font-bold ${color}`}>{val}</p>
                  <p className="text-gray-500 text-xs mt-1">{label}</p>
                </div>
              ))}
            </div>
          )}

          {/* Endpoints grouped */}
          {syncReport?.platform?.endpoints_grouped && (() => {
            const grouped = syncReport.platform.endpoints_grouped;
            const allEps = Object.values(grouped).flat();
            const filtered = epSearch
              ? allEps.filter(e => e.path?.toLowerCase().includes(epSearch.toLowerCase()) || e.method?.toLowerCase().includes(epSearch.toLowerCase()))
              : null;

            if (filtered) {
              return (
                <div className="glass-panel overflow-hidden">
                  <div className="divide-y divide-white/5">
                    {filtered.map((ep, i) => <EndpointRow key={i} ep={ep} stats={endpointData?.stats} />)}
                  </div>
                </div>
              );
            }

            return (
              <div className="space-y-2">
                {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([tag, eps]) => (
                  <div key={tag} className="glass-panel overflow-hidden">
                    <button onClick={() => setEpExpanded(x => ({ ...x, [tag]: !x[tag] }))}
                      className="w-full flex items-center justify-between p-3.5 hover:bg-white/3 transition-colors">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-white capitalize">{tag}</span>
                        <div className="flex gap-1">
                          {['GET','POST','PUT','DELETE'].map(m => {
                            const cnt = eps.filter(e => e.method === m).length;
                            if (!cnt) return null;
                            return <span key={m} className={`text-[10px] px-1.5 py-0.5 rounded border ${METHOD_COLORS[m]}`}>{cnt} {m}</span>;
                          })}
                        </div>
                      </div>
                      {epExpanded[tag] ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                    </button>
                    {epExpanded[tag] && (
                      <div className="border-t border-white/5 divide-y divide-white/3">
                        {eps.map((ep, i) => <EndpointRow key={i} ep={ep} stats={endpointData?.stats} />)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}

          {!syncReport?.platform?.endpoints_grouped && (
            <div className="glass-panel p-12 text-center">
              <Server className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500">Ejecuta el Escaneo Reverso para descubrir todos los endpoints de esta plataforma.</p>
            </div>
          )}
        </div>
      )}

      {/* ── OAUTH SSO TAB ── */}
      {activeTab === 'oauth' && (
        <div className="space-y-4">
          <div className="flex gap-3 flex-wrap">
            <button onClick={handleConfigureOAuth} disabled={loading.oauth}
              className="btn-primary flex items-center gap-2 text-sm">
              {loading.oauth ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
              Configurar OAuth SSO
            </button>
            <button onClick={handleTestOAuth} disabled={loading.oauthTest}
              className="btn-secondary flex items-center gap-2 text-sm">
              {loading.oauthTest ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              Probar SSO
            </button>
          </div>

          {oauthStatus?.config ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <StatusBadge status={oauthStatus.config.status || 'configured'} label={oauthStatus.config.status === 'active' ? 'SSO Activo' : 'Configurado'} />
                {oauthStatus.config.configured_at && (
                  <span className="text-gray-500 text-xs">Configurado: {oauthStatus.config.configured_at}</span>
                )}
              </div>

              {/* Bidirectional config */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { title: '🔵 Configuración Local (OkBot)', data: oauthStatus.config.local_config,  color: 'border-blue-500/30 bg-blue-500/5' },
                  { title: '🟣 Configuración Remota',        data: oauthStatus.config.remote_config, color: 'border-purple-500/30 bg-purple-500/5' },
                ].map(({ title, data, color }) => data && (
                  <div key={title} className={`glass-panel p-4 border ${color}`}>
                    <h4 className="text-sm font-semibold text-gray-200 mb-3">{title}</h4>
                    <div className="space-y-2 text-xs font-mono">
                      {[
                        ['client_id',          data.client_id],
                        ['client_secret',       data.client_secret],
                        ['redirect_uri',        data.redirect_uri],
                        ['authorization_url',   data.authorization_url],
                        ['token_url',           data.token_url],
                        ['userinfo_url',        data.userinfo_url],
                      ].map(([k, v]) => v && (
                        <div key={k} className="flex items-start gap-2">
                          <span className="text-gray-500 flex-shrink-0 w-32">{k}:</span>
                          <div className="flex items-center gap-1 flex-1 min-w-0">
                            <span className="text-gray-200 truncate flex-1">{v}</span>
                            <button onClick={() => copy(v)} className="p-0.5 text-gray-600 hover:text-white flex-shrink-0">
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    {data.instructions && (
                      <p className="text-gray-500 text-xs mt-3 italic border-t border-white/5 pt-2">{data.instructions}</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Next Steps */}
              {oauthStatus.config.next_steps && (
                <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4 space-y-2">
                  <p className="text-blue-400 text-xs font-semibold">📋 Siguientes pasos:</p>
                  {oauthStatus.config.next_steps.map((s, i) => (
                    <p key={i} className="text-gray-400 text-xs">{s}</p>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="glass-panel p-12 text-center">
              <Key className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 mb-2">OAuth SSO no configurado</p>
              <p className="text-gray-600 text-sm">Haz clic en "Configurar OAuth SSO" para generar las credenciales bidireccionales automáticamente.</p>
            </div>
          )}
        </div>
      )}

      {/* ── USERS TAB ── */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          <div className="flex gap-3 flex-wrap">
            <button onClick={handleSyncUsers} disabled={loading.users}
              className="btn-primary flex items-center gap-2 text-sm">
              {loading.users ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
              {loading.users ? 'Sincronizando...' : 'Sincronizar Usuarios'}
            </button>
          </div>

          {userSync?.sync ? (
            <div className="space-y-4">
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                  { label: 'Remotos',    val: userSync.sync.remote_total, color: 'text-blue-400' },
                  { label: 'Locales',    val: userSync.sync.local_total,  color: 'text-purple-400' },
                  { label: 'Vinculados', val: userSync.sync.matched,      color: 'text-green-400' },
                  { label: 'Nuevos',     val: userSync.sync.new_remote,   color: 'text-amber-400' },
                  { label: 'Conflictos', val: userSync.sync.conflicts,    color: 'text-red-400' },
                ].map(({ label, val, color }) => (
                  <div key={label} className="glass-panel p-4 text-center">
                    <p className={`text-2xl font-bold ${color}`}>{val}</p>
                    <p className="text-gray-500 text-xs mt-1">{label}</p>
                  </div>
                ))}
              </div>

              {/* Match rate bar */}
              <div className="glass-panel p-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-400">Tasa de vinculación</span>
                  <span className="text-white font-semibold">
                    {userSync.sync.remote_total > 0
                      ? Math.round(userSync.sync.matched / userSync.sync.remote_total * 100)
                      : 0}%
                  </span>
                </div>
                <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-green-500 rounded-full transition-all" style={{
                    width: `${userSync.sync.remote_total > 0 ? Math.round(userSync.sync.matched / userSync.sync.remote_total * 100) : 0}%`
                  }} />
                </div>
              </div>

              {/* Conflicts */}
              {userSync.sync.conflict_users?.length > 0 && (
                <div className="glass-panel p-4 border border-yellow-500/20 bg-yellow-500/5">
                  <h4 className="text-yellow-400 text-sm font-semibold mb-3">⚠️ Conflictos de Plan ({userSync.sync.conflict_users.length})</h4>
                  <div className="space-y-2">
                    {userSync.sync.conflict_users.map((c, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm bg-black/20 rounded-lg p-2">
                        <span className="text-white font-medium">{c.user}</span>
                        <span className="text-gray-500 text-xs">Local: <span className="text-blue-400">{c.local_plan}</span></span>
                        <ArrowRight className="w-3 h-3 text-gray-600" />
                        <span className="text-gray-500 text-xs">Remoto: <span className="text-purple-400">{c.remote_plan}</span></span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Linked users table */}
              {userSync.links?.length > 0 && (
                <div className="glass-panel overflow-hidden">
                  <div className="p-4 border-b border-white/5 flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-gray-200">Usuarios Vinculados ({userSync.links_count})</h4>
                    <StatusBadge status={userSync.sync.status} label={userSync.sync.status} />
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-white/5 text-gray-500">
                        <tr>
                          <th className="px-3 py-2 text-left">Usuario Local</th>
                          <th className="px-3 py-2 text-left hidden md:table-cell">Email</th>
                          <th className="px-3 py-2 text-center">Match</th>
                          <th className="px-3 py-2 text-center">Plan Local</th>
                          <th className="px-3 py-2 text-center">Plan Remoto</th>
                          <th className="px-3 py-2 text-center">Conflicto</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {userSync.links.slice(0, 30).map((u, i) => (
                          <tr key={i} className="hover:bg-white/3">
                            <td className="px-3 py-2 font-mono text-white">{u.local_okuser || u.local_id?.slice(0, 8)}</td>
                            <td className="px-3 py-2 text-gray-400 hidden md:table-cell">{u.email || '—'}</td>
                            <td className="px-3 py-2 text-center">
                              <span className="text-xs text-blue-400 bg-blue-500/10 px-1.5 py-0.5 rounded">{u.match_method}</span>
                            </td>
                            <td className="px-3 py-2 text-center text-gray-300">{u.plan_local}</td>
                            <td className="px-3 py-2 text-center text-gray-300">{u.plan_remote}</td>
                            <td className="px-3 py-2 text-center">
                              {u.conflict
                                ? <AlertTriangle className="w-3.5 h-3.5 text-yellow-400 mx-auto" />
                                : <CheckCircle2 className="w-3.5 h-3.5 text-green-400 mx-auto" />}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="glass-panel p-12 text-center">
              <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 mb-2">Usuarios no sincronizados</p>
              <p className="text-gray-600 text-sm">Ejecuta la sincronización para vincular usuarios de ambas plataformas.</p>
            </div>
          )}
        </div>
      )}

      {/* ── WALLET TAB ── */}
      {activeTab === 'wallet' && (
        <div className="space-y-4">
          <div className="flex gap-3 flex-wrap">
            <button onClick={handleSyncWallet} disabled={loading.wallet}
              className="btn-primary flex items-center gap-2 text-sm">
              {loading.wallet ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />}
              {loading.wallet ? 'Sincronizando...' : 'Sincronizar OkWallet'}
            </button>
          </div>

          {walletReport?.report ? (() => {
            const r = walletReport.report;
            return (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: 'Wallets Remotas',    val: r.remote_wallets,    color: 'text-blue-400' },
                    { label: 'Wallets Locales',    val: walletReport.report.remote_wallets, color: 'text-purple-400' },
                    { label: 'Sincronizadas',      val: r.matched_wallets,   color: 'text-green-400' },
                    { label: 'Diffs de Saldo',     val: r.balance_diffs,     color: r.balance_diffs > 0 ? 'text-yellow-400' : 'text-green-400' },
                  ].map(({ label, val, color }) => (
                    <div key={label} className="glass-panel p-4 text-center">
                      <p className={`text-2xl font-bold ${color}`}>{val ?? 0}</p>
                      <p className="text-gray-500 text-xs mt-1">{label}</p>
                    </div>
                  ))}
                </div>

                {/* Transaction breakdown */}
                <div className="glass-panel p-4">
                  <h4 className="text-sm font-semibold text-gray-200 mb-3">📊 Transacciones Remotas</h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    {[
                      { label: 'Recargas',      val: r.remote_recharges,  icon: TrendingUp,   color: 'text-green-400' },
                      { label: 'Transferencias', val: r.remote_transfers,  icon: ArrowRight,   color: 'text-blue-400' },
                      { label: 'Retiros',        val: r.remote_withdrawals, icon: TrendingDown, color: 'text-red-400' },
                    ].map(({ label, val, icon: Icon, color }) => (
                      <div key={label} className="bg-white/5 rounded-xl p-3">
                        <Icon className={`w-5 h-5 mx-auto mb-2 ${color}`} />
                        <p className={`text-lg font-bold ${color}`}>{val ?? 0}</p>
                        <p className="text-gray-500 text-xs">{label}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Balance diffs */}
                {r.balance_diff_details?.length > 0 && (
                  <div className="glass-panel p-4 border border-yellow-500/20 bg-yellow-500/5">
                    <h4 className="text-yellow-400 text-sm font-semibold mb-3">⚠️ Diferencias de Saldo</h4>
                    <div className="space-y-2">
                      {r.balance_diff_details.map((d, i) => (
                        <div key={i} className="flex items-center justify-between bg-black/20 rounded-lg p-2 text-sm">
                          <span className="text-gray-300 font-mono text-xs">{d.local_wallet_id}</span>
                          <span className="text-yellow-400 font-semibold">Δ ${d.difference.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Configs comparison */}
                {r.remote_config && Object.keys(r.remote_config).length > 0 && (
                  <div className="glass-panel p-4">
                    <h4 className="text-sm font-semibold text-gray-200 mb-3">⚙️ Configuración Remota OkWallet</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                      {Object.entries(r.remote_config).filter(([k]) => !['_id','key'].includes(k)).map(([k, v]) => (
                        <div key={k} className="bg-black/20 rounded-lg p-2">
                          <p className="text-gray-500 capitalize">{k.replace(/_/g, ' ')}</p>
                          <p className="text-white font-semibold">{typeof v === 'number' ? `${v}%` : String(v)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {r.fetch_errors?.length > 0 && (
                  <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-3">
                    <p className="text-red-400 text-xs font-semibold mb-1">Errores de sincronización:</p>
                    {r.fetch_errors.map((e, i) => <p key={i} className="text-gray-500 text-xs">{e}</p>)}
                  </div>
                )}
              </div>
            );
          })() : (
            <div className="glass-panel p-12 text-center">
              <Wallet className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 mb-2">OkWallet no sincronizado</p>
              <p className="text-gray-600 text-sm">Sincroniza para unificar saldos, recargas y retiros entre plataformas.</p>
            </div>
          )}
        </div>
      )}

      {/* ── WEBHOOKS TAB ── */}
      {activeTab === 'webhooks' && (
        <div className="space-y-4">
          <div className="flex gap-3 flex-wrap">
            <button onClick={handleCreateWebhooks} disabled={loading.webhooks}
              className="btn-primary flex items-center gap-2 text-sm">
              {loading.webhooks ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Webhook className="w-4 h-4" />}
              {loading.webhooks ? 'Generando...' : 'Generar Todos los Webhooks'}
            </button>
          </div>

          {webhooks ? (
            <div className="space-y-4">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Total',    val: webhooks.total,   color: 'text-white' },
                  { label: 'Activos',  val: webhooks.active,  color: 'text-green-400' },
                  { label: 'Pendientes', val: webhooks.pending, color: 'text-yellow-400' },
                ].map(({ label, val, color }) => (
                  <div key={label} className="glass-panel p-4 text-center">
                    <p className={`text-2xl font-bold ${color}`}>{val}</p>
                    <p className="text-gray-500 text-xs mt-1">{label}</p>
                  </div>
                ))}
              </div>

              {/* By category */}
              {Object.entries(webhooks.by_category || {}).map(([cat, whs]) => (
                <div key={cat} className="glass-panel overflow-hidden">
                  <div className="p-3 border-b border-white/5 flex items-center gap-2">
                    <span className="text-sm font-semibold text-white capitalize">{cat}</span>
                    <span className="text-gray-500 text-xs">{whs.length} eventos</span>
                  </div>
                  <div className="divide-y divide-white/5">
                    {whs.map(wh => (
                      <div key={wh.id} className="p-3 flex items-center gap-3 hover:bg-white/3 transition-colors">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-white text-sm font-mono">{wh.event}</span>
                            <StatusBadge status={wh.status === 'active' ? 'ok' : 'warning'} label={wh.status} />
                          </div>
                          <p className="text-gray-500 text-xs truncate">{wh.send_url}</p>
                          {wh.last_triggered && (
                            <p className="text-gray-600 text-xs">Último: {wh.last_triggered} · {wh.trigger_count} disparos</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button onClick={() => copy(wh.receive_url)} className="p-1.5 text-gray-500 hover:text-white hover:bg-white/10 rounded" title="Copiar URL">
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => handleTestWebhook(wh.id)} disabled={loading[`wh_${wh.id}`]}
                            className="p-1.5 text-blue-400 hover:bg-blue-500/20 rounded transition-colors" title="Probar webhook">
                            {loading[`wh_${wh.id}`] ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="glass-panel p-12 text-center">
              <Webhook className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 mb-2">Sin webhooks generados</p>
              <p className="text-gray-600 text-sm">Genera los webhooks para mantener ambas plataformas sincronizadas en tiempo real.</p>
            </div>
          )}
        </div>
      )}

      {/* ── OKBOT AGENT TAB ── */}
      {activeTab === 'agent' && (
        <div className="space-y-4">
          {/* Action buttons */}
          <div className="flex gap-3 flex-wrap">
            <button onClick={handlePushReverseLink} disabled={loading.reverseLink}
              className="btn-secondary flex items-center gap-2 text-sm">
              {loading.reverseLink ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
              {loading.reverseLink ? 'Vinculando...' : 'Registrar OkBot en Plataforma Remota'}
            </button>
            <button onClick={handleInstallAgent} disabled={loading.agentInstall}
              className="btn-primary flex items-center gap-2 text-sm">
              {loading.agentInstall ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />}
              {loading.agentInstall ? 'Instalando OkBot...' : 'Instalar OkBot como Agente Principal'}
            </button>
          </div>

          {/* Concept explanation */}
          <div className="glass-panel p-4 border border-blue-500/20 bg-blue-500/5">
            <h4 className="text-blue-400 text-sm font-semibold mb-2">🤖 ¿Cómo funciona la instalación del agente?</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-gray-400">
              {[
                ['1. Vinculación Reversa', 'OkBot se registra como plataforma vinculada en el sistema remoto — la plataforma secundaria verá a OkBot como plataforma principal conectada.'],
                ['2. Detección de Agentes', 'El sistema escanea los endpoints de agentes IA en la plataforma remota y los identifica.'],
                ['3. Registro como Primario', 'OkBot se registra con prioridad 1 (primario). Accede a todos los módulos: chat, wallet, tareas, salud, comercio, soporte.'],
                ['4. Relegación de Secundarios', 'Los agentes existentes quedan como secundarios (priority: 99). OkBot toma el control del módulo de IA.'],
                ['5. Webhook de Chat', 'Se configura webhook para que todos los mensajes de chat lleguen a OkBot primero.'],
                ['6. Script de Embed', 'Se genera el script para instalar el widget de OkBot en la interfaz de la plataforma remota.'],
              ].map(([title, desc]) => (
                <div key={title} className="bg-black/20 rounded-lg p-2.5">
                  <p className="text-blue-300 font-semibold mb-1">{title}</p>
                  <p>{desc}</p>
                </div>
              ))}
            </div>
          </div>

          {agentStatus ? (
            <div className="space-y-4">
              {/* Score + Status */}
              <div className="glass-panel p-5">
                <div className="flex items-center gap-5 mb-4">
                  <ScoreRing score={agentStatus.installation_score} size={80} />
                  <div>
                    <h3 className="text-white font-semibold text-lg">
                      {agentStatus.registered ? '✅ OkBot instalado como Agente Primario' : '⚠️ Instalación Parcial'}
                    </h3>
                    <p className="text-gray-400 text-sm mt-1">
                      {agentStatus.existing_agents} agentes previos detectados · {agentStatus.demoted_agents || 0} marcados como secundarios
                    </p>
                    <p className="text-gray-600 text-xs mt-1">Instalado: {agentStatus.installed_at}</p>
                  </div>
                </div>

                {/* Steps checklist */}
                <div className="space-y-2">
                  {(agentStatus.steps || []).map((step, i) => {
                    const cfg = STATUS_COLORS[step.status] || STATUS_COLORS.pending;
                    const Icon = cfg.icon;
                    return (
                      <div key={i} className={`flex items-start gap-3 p-3 rounded-xl border ${cfg.bg}`}>
                        <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${cfg.text}`} />
                        <div className="flex-1">
                          <p className={`text-xs font-semibold ${cfg.text} capitalize`}>{step.step.replace(/_/g,' ')}</p>
                          <p className="text-gray-400 text-xs mt-0.5">{step.detail}</p>
                          {step.embed_code && (
                            <div className="mt-2 bg-black/40 rounded-lg p-2 flex items-start gap-2">
                              <code className="text-green-300 text-[10px] flex-1 break-all font-mono">{step.embed_code}</code>
                              <button onClick={() => copy(step.embed_code)} className="p-1 text-gray-500 hover:text-white flex-shrink-0">
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                          {step.path && <p className="text-gray-600 text-[10px] font-mono mt-1">→ {step.path}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Reverse Link Status */}
              {syncRegistry?.platform_flags && (
                <div className="glass-panel p-4">
                  <h4 className="text-sm font-semibold text-gray-200 mb-3">🔗 Estado de Vinculación Bidireccional</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {[
                      { label: 'Link Reverso Push', val: syncRegistry.platform_flags.reverse_link_pushed, type: 'bool' },
                      { label: 'OkBot Instalado',   val: syncRegistry.platform_flags.okbot_installed,     type: 'bool' },
                      { label: 'Webhooks',           val: syncRegistry.platform_flags.webhooks_generated,  type: 'bool' },
                    ].map(({ label, val, type }) => (
                      <div key={label} className="bg-white/5 rounded-xl p-3 flex items-center gap-3">
                        {val
                          ? <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                          : <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />}
                        <p className="text-gray-300 text-xs">{label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="glass-panel p-12 text-center">
              <Bot className="w-14 h-14 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg font-medium mb-2">OkBot Agent no instalado</p>
              <p className="text-gray-600 text-sm max-w-md mx-auto">
                Primero registra OkBot como plataforma vinculada en el sistema remoto,
                luego instálalo como agente principal para tomar control del módulo de IA.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── MONITOR CONTINUO TAB ── */}
      {activeTab === 'monitor' && (
        <div className="space-y-4">
          <div className="flex gap-3 flex-wrap items-center">
            <button onClick={handleRunMonitor} disabled={loading.monitor}
              className="btn-primary flex items-center gap-2 text-sm">
              {loading.monitor ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
              {loading.monitor ? 'Ejecutando monitor...' : 'Ejecutar Monitor Ahora'}
            </button>
            <div className="flex-1">
              <p className="text-gray-500 text-xs">
                El monitor detecta cambios en la plataforma remota, registra nuevos endpoints y crea webhooks faltantes automáticamente.
              </p>
            </div>
          </div>

          {monitorStatus ? (
            <div className="space-y-4">
              {/* Status Header */}
              <div className={`glass-panel p-4 border ${monitorStatus.status === 'clean' ? 'border-green-500/30 bg-green-500/5' : 'border-amber-500/30 bg-amber-500/5'}`}>
                <div className="flex items-center gap-3">
                  {monitorStatus.status === 'clean'
                    ? <CheckCircle2 className="w-6 h-6 text-green-400" />
                    : <AlertTriangle className="w-6 h-6 text-amber-400" />}
                  <div>
                    <p className={`font-semibold ${monitorStatus.status === 'clean' ? 'text-green-300' : 'text-amber-300'}`}>
                      {monitorStatus.status === 'clean' ? 'Sin cambios — Todo sincronizado' : 'Cambios detectados y aplicados automáticamente'}
                    </p>
                    <p className="text-gray-500 text-xs">Último monitor: {monitorStatus.ran_at}</p>
                  </div>
                </div>
              </div>

              {/* Quick counters */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: 'Endpoints Nuevos',      val: monitorStatus.new_endpoints || 0,             color: monitorStatus.new_endpoints > 0 ? 'text-blue-400' : 'text-gray-500' },
                  { label: 'Webhooks Auto-creados', val: monitorStatus.missing_webhooks_fixed || 0,     color: monitorStatus.missing_webhooks_fixed > 0 ? 'text-green-400' : 'text-gray-500' },
                  { label: 'Hallazgos',             val: (monitorStatus.findings || []).length,         color: 'text-amber-400' },
                  { label: 'Acciones Tomadas',      val: (monitorStatus.actions || []).length,          color: 'text-purple-400' },
                ].map(({ label, val, color }) => (
                  <div key={label} className="glass-panel p-4 text-center">
                    <p className={`text-2xl font-bold ${color}`}>{val}</p>
                    <p className="text-gray-500 text-xs mt-1">{label}</p>
                  </div>
                ))}
              </div>

              {/* Findings */}
              {(monitorStatus.findings || []).length > 0 && (
                <div className="glass-panel overflow-hidden">
                  <div className="p-3 border-b border-white/5">
                    <h4 className="text-sm font-semibold text-gray-200">📋 Hallazgos del Monitor</h4>
                  </div>
                  <div className="divide-y divide-white/5">
                    {monitorStatus.findings.map((f, i) => {
                      const typeColors = {
                        new_endpoints:  { icon: Server,      color: 'text-blue-400',   bg: 'bg-blue-500/5' },
                        missing_webhooks:{ icon: Webhook,    color: 'text-green-400',  bg: 'bg-green-500/5' },
                        user_drift:     { icon: Users,       color: 'text-amber-400',  bg: 'bg-amber-500/5' },
                        connectivity:   { icon: Globe,       color: f.status === 'ok' ? 'text-green-400' : 'text-red-400', bg: f.status === 'ok' ? 'bg-green-500/5' : 'bg-red-500/5' },
                      };
                      const cfg = typeColors[f.type] || { icon: Activity, color: 'text-gray-400', bg: '' };
                      const Icon = cfg.icon;
                      return (
                        <div key={i} className={`p-3 flex items-start gap-3 ${cfg.bg}`}>
                          <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${cfg.color}`} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs font-semibold ${cfg.color} capitalize`}>
                                {f.type.replace(/_/g, ' ')}
                              </span>
                              {f.count !== undefined && (
                                <span className={`text-xs px-1.5 py-0.5 rounded-full bg-black/30 ${cfg.color}`}>{f.count}</span>
                              )}
                            </div>
                            <p className="text-gray-400 text-xs">{f.detail}</p>
                            {f.requires_resync && (
                              <p className="text-amber-400 text-xs mt-1">⚠️ Se recomienda ejecutar re-sync de usuarios</p>
                            )}
                            {f.items && f.items.length > 0 && (
                              <div className="mt-1.5 flex flex-wrap gap-1">
                                {f.items.slice(0, 5).map((item, j) => (
                                  <span key={j} className="text-[10px] font-mono bg-black/30 px-1.5 py-0.5 rounded text-gray-400">
                                    {typeof item === 'object' ? `${item.method} ${item.path}` : item}
                                  </span>
                                ))}
                                {f.items.length > 5 && <span className="text-[10px] text-gray-600">+{f.items.length - 5} más</span>}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Actions taken */}
              {(monitorStatus.actions || []).length > 0 && (
                <div className="glass-panel p-4 border border-green-500/20 bg-green-500/5">
                  <h4 className="text-green-400 text-sm font-semibold mb-3">⚡ Acciones Automáticas Ejecutadas</h4>
                  <div className="space-y-2">
                    {monitorStatus.actions.map((a, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                        <span className="text-gray-300 capitalize">{a.action.replace(/_/g, ' ')}</span>
                        {a.count !== undefined && (
                          <span className="text-green-400 font-semibold">{a.count} elementos</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="glass-panel p-12 text-center">
              <Activity className="w-14 h-14 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg font-medium mb-2">Monitor no ejecutado</p>
              <p className="text-gray-600 text-sm max-w-md mx-auto">
                El monitor detecta automáticamente nuevos endpoints, webhooks faltantes y cambios
                en usuarios de la plataforma remota. Ejecuta una vez para ver el estado.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── REGISTRO & RIESGO TAB ── */}
      {activeTab === 'registry' && (
        <div className="space-y-4">
          {/* Safe Resync Panel */}
          <div className="glass-panel p-4">
            <h4 className="text-sm font-semibold text-gray-200 mb-3">🛡️ Re-sync Seguro con Control de Riesgo</h4>
            <p className="text-gray-500 text-xs mb-4">
              El re-sync seguro hace snapshot de todo antes de sincronizar, valida cada módulo con gates de seguridad
              y aborta si alguna condición de riesgo no se cumple. Sin pérdida de datos garantizada.
            </p>

            {/* Module selection */}
            <div className="mb-4">
              <p className="text-gray-400 text-xs font-semibold mb-2">Módulos a sincronizar:</p>
              <div className="flex flex-wrap gap-2">
                {['endpoints','users','wallet','webhooks'].map(mod => (
                  <button key={mod} onClick={() => setSafeModules(prev =>
                    prev.includes(mod) ? prev.filter(m => m !== mod) : [...prev, mod]
                  )}
                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-colors ${
                      safeModules.includes(mod)
                        ? 'bg-red-500/20 text-red-300 border-red-500/30'
                        : 'bg-white/5 text-gray-500 border-white/10'
                    }`}>
                    {mod.charAt(0).toUpperCase() + mod.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Dry run toggle */}
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => setSafeResyncDry(!safeResyncDry)}
                className={`relative w-10 h-5 rounded-full transition-colors ${safeResyncDry ? 'bg-blue-500' : 'bg-gray-600'}`}>
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${safeResyncDry ? 'left-0.5' : 'left-5'}`} />
              </button>
              <div>
                <p className="text-gray-300 text-sm font-medium">{safeResyncDry ? '🔍 Modo Simulación (Dry Run)' : '⚡ Modo Real — Ejecutará cambios'}</p>
                <p className="text-gray-600 text-xs">{safeResyncDry ? 'Solo analiza, no modifica nada' : 'Aplicará cambios reales en la plataforma'}</p>
              </div>
            </div>

            <button onClick={handleSafeResync} disabled={loading.safeResync || safeModules.length === 0}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                safeResyncDry
                  ? 'bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 border border-blue-500/30'
                  : 'bg-red-500/20 text-red-300 hover:bg-red-500/30 border border-red-500/30'
              } disabled:opacity-40`}>
              {loading.safeResync ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
              {loading.safeResync
                ? 'Ejecutando re-sync seguro...'
                : safeResyncDry
                  ? `Simular Re-sync (${safeModules.length} módulos)`
                  : `Ejecutar Re-sync Real (${safeModules.length} módulos)`}
            </button>
          </div>

          {/* Platform Flags Dashboard */}
          {syncRegistry?.platform_flags && (
            <div className="glass-panel p-4">
              <h4 className="text-sm font-semibold text-gray-200 mb-3">🏳️ Estado General de la Plataforma</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { label: 'Link Reverso',     val: syncRegistry.platform_flags.reverse_link_pushed },
                  { label: 'OkBot Instalado',  val: syncRegistry.platform_flags.okbot_installed },
                  { label: 'Webhooks Creados', val: syncRegistry.platform_flags.webhooks_generated },
                  { label: 'Usuarios Sync',    val: syncRegistry.platform_flags.user_sync_status === 'synced' },
                  { label: 'Wallet Sync',      val: syncRegistry.platform_flags.wallet_sync_status === 'synced' },
                  { label: 'Monitor OK',       val: syncRegistry.platform_flags.monitor_status === 'clean' },
                ].map(({ label, val }) => (
                  <div key={label} className={`p-3 rounded-xl border flex items-center gap-2 ${
                    val ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'
                  }`}>
                    {val
                      ? <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                      : <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />}
                    <span className="text-xs text-gray-300">{label}</span>
                  </div>
                ))}
              </div>
              {syncRegistry.platform_flags.last_monitor && (
                <p className="text-gray-600 text-xs mt-3">Último monitor: {syncRegistry.platform_flags.last_monitor}</p>
              )}
            </div>
          )}

          {/* Sync Registry History */}
          {syncRegistry?.registry?.length > 0 && (
            <div className="glass-panel overflow-hidden">
              <div className="p-3 border-b border-white/5">
                <h4 className="text-sm font-semibold text-gray-200">📜 Historial de Operaciones de Sync</h4>
              </div>
              <div className="divide-y divide-white/5 max-h-80 overflow-y-auto">
                {syncRegistry.registry.map((entry, i) => (
                  <div key={i} className="p-3 hover:bg-white/3 transition-colors">
                    <div className="flex items-center gap-3 mb-1">
                      <StatusBadge
                        status={entry.status === 'completed' ? 'ok' : entry.status === 'running' ? 'pending' : 'warning'}
                        label={entry.status}
                      />
                      {entry.dry_run && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded border border-blue-500/30">DRY RUN</span>
                      )}
                      <span className="text-gray-600 text-xs ml-auto">{entry.started_at}</span>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(entry.modules || []).map(m => (
                        <span key={m} className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${
                          entry.results?.[m] === 'resynced'
                            ? 'bg-green-500/20 text-green-400'
                            : entry.results?.[m] === 'gate_failed'
                              ? 'bg-red-500/20 text-red-400'
                              : 'bg-gray-500/20 text-gray-400'
                        }`}>
                          {m}: {entry.results?.[m] || 'pending'}
                        </span>
                      ))}
                    </div>
                    {entry.log?.some(l => !l.gate_pass) && (
                      <p className="text-yellow-500 text-xs mt-1">
                        ⚠️ Gates fallidas: {entry.log.filter(l => !l.gate_pass).map(l => l.module).join(', ')}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Anti-failure Risk Info */}
          <div className="glass-panel p-4 border border-purple-500/20 bg-purple-500/5">
            <h4 className="text-purple-400 text-sm font-semibold mb-3">🔒 Sistema Anti-Fallas</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-gray-400">
              {[
                ['Snapshot Pre-sync', 'Antes de cada operación se crea un snapshot completo de usuarios, wallets y webhooks sincronizados para rollback instantáneo.'],
                ['Gates de Validación', 'Cada módulo tiene condiciones mínimas: conectividad, API key, datos previos requeridos. Si una gate falla, el módulo se omite sin afectar los demás.'],
                ['Upsert Semántico', 'Todas las escrituras son upsert (update-or-insert), nunca delete-and-recreate. Garantiza idempotencia total.'],
                ['Semáforos de Concurrencia', 'El escaneo de endpoints usa semaphores para no saturar la plataforma remota (máx. 8 conexiones paralelas).'],
              ].map(([title, desc]) => (
                <div key={title} className="bg-black/20 rounded-lg p-2.5">
                  <p className="text-purple-300 font-semibold mb-1">{title}</p>
                  <p>{desc}</p>
                </div>
              ))}
            </div>
          </div>

          {!syncRegistry?.registry?.length && (
            <div className="glass-panel p-8 text-center">
              <Database className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 mb-2">Sin operaciones en el registro</p>
              <p className="text-gray-600 text-sm">El historial de sincronizaciones aparecerá aquí después de ejecutar operaciones.</p>
            </div>
          )}
        </div>
      )}

      {/* ── VERIFICATION TAB ── */}
      {activeTab === 'verify' && (
        <div className="space-y-4">
          <button onClick={handleVerify} disabled={loading.verify}
            className="btn-primary flex items-center gap-2 text-sm">
            {loading.verify ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
            {loading.verify ? 'Verificando todo el sistema...' : 'Ejecutar Verificación Completa'}
          </button>

          {verification ? (
            <div className="space-y-4">
              {/* Score card */}
              <div className="glass-panel p-6">
                <div className="flex items-center gap-6 mb-6">
                  <ScoreRing score={verification.overall_score} size={100} />
                  <div>
                    <h3 className="text-white font-bold text-2xl">{verification.overall_score}% Funcional</h3>
                    <p className="text-gray-400 capitalize text-sm mt-1">{verification.health?.replace('_', ' ')}</p>
                    <div className="flex gap-4 mt-3 text-sm">
                      <span className="flex items-center gap-1 text-green-400"><CheckCircle2 className="w-4 h-4" />{verification.summary.ok} OK</span>
                      <span className="flex items-center gap-1 text-yellow-400"><AlertTriangle className="w-4 h-4" />{verification.summary.warnings} warnings</span>
                      <span className="flex items-center gap-1 text-red-400"><XCircle className="w-4 h-4" />{verification.summary.errors} errores</span>
                    </div>
                  </div>
                </div>

                {/* Checks detail */}
                <div className="space-y-3">
                  {(verification.checks || []).map(c => {
                    const cfg = STATUS_COLORS[c.status] || STATUS_COLORS.pending;
                    const Icon = cfg.icon;
                    return (
                      <div key={c.name} className={`p-4 rounded-xl border ${cfg.bg}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3 flex-1">
                            <Icon className={`w-5 h-5 flex-shrink-0 ${cfg.text}`} />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="text-white font-medium text-sm">{c.name}</p>
                                <StatusBadge status={c.status} label={c.status} />
                              </div>
                              <p className="text-gray-400 text-xs">{c.detail}</p>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className={`font-bold text-lg ${cfg.text}`}>{c.score}%</p>
                            <div className="w-16 h-1.5 bg-white/10 rounded-full mt-1">
                              <div className="h-full rounded-full" style={{
                                width: `${c.score}%`,
                                background: c.status === 'ok' ? '#22C55E' : c.status === 'warning' ? '#F59E0B' : '#EF4444'
                              }} />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Next steps */}
              {verification.next_steps?.length > 0 && (
                <div className="glass-panel p-5 border border-blue-500/20 bg-blue-500/5">
                  <h4 className="text-blue-400 font-semibold text-sm mb-3">📋 Plan de acción para 100%</h4>
                  <ol className="space-y-2">
                    {verification.next_steps.map((s, i) => (
                      <li key={i} className="flex gap-3 text-sm">
                        <span className="text-blue-500 font-bold flex-shrink-0">{i+1}.</span>
                        <span className="text-gray-300">{s}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              <p className="text-gray-600 text-xs text-right">Última verificación: {verification.verified_at}</p>
            </div>
          ) : (
            <div className="glass-panel p-12 text-center">
              <Shield className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 mb-2">Sin verificación ejecutada</p>
              <p className="text-gray-600 text-sm">Ejecuta la verificación completa para validar que todos los sistemas de sincronización estén 100% operativos.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Endpoint Row subcomponent ─────────────────────────────────────────────────
function EndpointRow({ ep, stats = [] }) {
  const stat = stats.find(s => s.key === `${ep.method}:${ep.path}`);
  const isAvailable = ep.available !== false;
  return (
    <div className={`flex items-center gap-3 p-2.5 hover:bg-white/3 transition-colors ${!isAvailable ? 'opacity-40' : ''}`}>
      <span className={`text-[10px] font-bold px-2 py-0.5 rounded border flex-shrink-0 ${METHOD_COLORS[ep.method] || 'bg-gray-500/20 text-gray-300 border-gray-500/30'}`}>
        {ep.method}
      </span>
      <code className="text-gray-200 text-xs flex-1 min-w-0 truncate font-mono">{ep.path}</code>
      {ep.requires_auth && <Lock className="w-3 h-3 text-yellow-400 flex-shrink-0" />}
      {ep.source === 'openapi' && (
        <span className="text-[9px] px-1 py-0.5 bg-blue-500/20 text-blue-400 rounded flex-shrink-0">OpenAPI</span>
      )}
      {stat ? (
        <div className="flex items-center gap-2 flex-shrink-0 text-xs text-gray-500">
          <span className="text-gray-300">{stat.total_calls} calls</span>
          {stat.total_calls > 0 && (
            <span className={stat.successful_calls / stat.total_calls > 0.9 ? 'text-green-400' : 'text-yellow-400'}>
              {Math.round(stat.successful_calls / stat.total_calls * 100)}%
            </span>
          )}
        </div>
      ) : (
        <span className={`text-[10px] flex-shrink-0 ${isAvailable ? 'text-green-500' : 'text-red-500'}`}>
          {isAvailable ? '✓' : '✗'}
        </span>
      )}
    </div>
  );
}
